/**
 * Banlist Manager — push and version Yu-Gi-Oh! format banlists.
 *
 * Two tabs: Advance Format (adv) and Edison Format (edi). Each tab has:
 *  • A push form — version name, effective date, change notes, and the card
 *    list as JSON ({ "Card Name": "Forbidden" | "Limited" | "Semi-Limited" }).
 *  • A history log showing every previously pushed version with its card
 *    count, who pushed it, and when.
 *
 * Cards not on the list at all are unrestricted — you only need to include
 * cards with a status. Mobile/web fetch GET /api/config/banlist/{fmt} at
 * startup and flag matching cards in the deck builder.
 */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button } from "../components/ui/button";
import PageHeader from "../components/ui/PageHeader";
import StatusBadge from "../components/ui/StatusBadge";
import { SkeletonTable } from "../components/ui/Skeleton";
import {
  Upload, RotateCcw, CheckCircle2, History, ChevronDown, ChevronUp, PackageOpen,
} from "lucide-react";
import { apiGetBanlist, apiPutBanlist } from "../lib/cloudSync";

// ---------------------------------------------------------------------------
// Bundled default lists (seeded from TCG list updated 05/18/2026)
// These are used as the "load bundled" baseline — they never go live on their
// own; an admin must explicitly push.
// ---------------------------------------------------------------------------

const BUNDLED_ADV = {
  version_name: "May 2026",
  effective_date: "2026-05-18",
  change_notes: "TCG Advanced Format list effective 2026-05-18.",
  cards: {
    // Forbidden
    "Abyss Dweller": "Forbidden",
    "Agido the Ancient Sentinel": "Forbidden",
    "Apollousa, Bow of the Goddess": "Forbidden",
    "Archnemeses Protos": "Forbidden",
    "Artifact Mjollnir": "Forbidden",
    "Artifact Scythe": "Forbidden",
    "Bahamut Shark": "Forbidden",
    "Baronne de Fleur": "Forbidden",
    "Barrier Statue of the Drought": "Forbidden",
    "Barrier Statue of the Inferno": "Forbidden",
    "Barrier Statue of the Stormwinds": "Forbidden",
    "Barrier Statue of the Torrent": "Forbidden",
    "Beatrice, Lady of the Eternal": "Forbidden",
    "Blackwing - Gofu the Vague Shadow": "Forbidden",
    "Block Dragon": "Forbidden",
    "Borreload Savage Dragon": "Forbidden",
    "Chaos Ruler, the Chaotic Magical Dragon": "Forbidden",
    "Crystron Halqifibrax": "Forbidden",
    "Curious, the Lightsworn Dominion": "Forbidden",
    "CXyz Gimmick Puppet Fanatix Machinix": "Forbidden",
    "Cyber-Stein": "Forbidden",
    "Dandylion": "Forbidden",
    "Djinn Releaser of Rituals": "Forbidden",
    "Eclipse Wyvern": "Forbidden",
    "Elder Entity Norden": "Forbidden",
    "Evilswarm Ouroboros": "Forbidden",
    "Fiber Jar": "Forbidden",
    "Fishborg Blaster": "Forbidden",
    "Fossil Dyna Pachycephalo": "Forbidden",
    "Gimmick Puppet Nightmare": "Forbidden",
    "Grinder Golem": "Forbidden",
    "Guardragon Agarpain": "Forbidden",
    "Guardragon Elpy": "Forbidden",
    "Heavymetalfoes Electrumite": "Forbidden",
    "Herald of the Arc Light": "Forbidden",
    "Hot Red Dragon Archfiend King Calamity": "Forbidden",
    "Isolde, Two Tales of the Noble Knights": "Forbidden",
    "Jowgen the Spiritualist": "Forbidden",
    "K9-04 Noroi": "Forbidden",
    "Kashtira Arise-Heart": "Forbidden",
    "Kelbek the Ancient Vanguard": "Forbidden",
    "King of the Feral Imps": "Forbidden",
    "Knightmare Goblin": "Forbidden",
    "Knightmare Gryphon": "Forbidden",
    "Knightmare Mermaid": "Forbidden",
    "Lavalval Chain": "Forbidden",
    "Level Eater": "Forbidden",
    "Link Decoder": "Forbidden",
    "Linkross": "Forbidden",
    "M-X-Saber Invoker": "Forbidden",
    "Magical Scientist": "Forbidden",
    "Maliss <Q> White Binder": "Forbidden",
    "Maxx \"C\"": "Forbidden",
    "Mecha Phantom Beast Auroradon": "Forbidden",
    "Mind Master": "Forbidden",
    "Moon of the Closed Heaven": "Forbidden",
    "Naturia Rosewhip": "Forbidden",
    "Number 16: Shock Master": "Forbidden",
    "Number 42: Galaxy Tomahawk": "Forbidden",
    "Number 67: Pair-a-Dice Smasher": "Forbidden",
    "Number 86: Heroic Champion - Rhongomyniad": "Forbidden",
    "Number 89: Diablosis the Mind Hacker": "Forbidden",
    "Number 95: Galaxy-Eyes Dark Matter Dragon": "Forbidden",
    "Number S0: Utopic ZEXAL": "Forbidden",
    "Outer Entity Azathot": "Forbidden",
    "Phoenixian Cluster Amaryllis": "Forbidden",
    "Prank-Kids Meow-Meow-Mu": "Forbidden",
    "Predaplant Verte Anaconda": "Forbidden",
    "Ronintoadin": "Forbidden",
    "Simorgh, Bird of Sovereignty": "Forbidden",
    "Splash Mage": "Forbidden",
    "Spright Elf": "Forbidden",
    "SPYRAL Master Plan": "Forbidden",
    "Superheavy Samurai Scarecrow": "Forbidden",
    "Supreme King Dragon Starving Venom": "Forbidden",
    "Tearlaments Kitkallos": "Forbidden",
    "Tempest Magician": "Forbidden",
    "The Tyrant Neptune": "Forbidden",
    "Topologic Gumblar Dragon": "Forbidden",
    "True King of All Calamities": "Forbidden",
    "Union Carrier": "Forbidden",
    "Victory Dragon": "Forbidden",
    "Wind-Up Carrier Zenmaity": "Forbidden",
    "Zoodiac Broadbull": "Forbidden",
    "Butterfly Dagger - Elma": "Forbidden",
    "Card of Safe Return": "Forbidden",
    "Cold Wave": "Forbidden",
    "Confiscation": "Forbidden",
    "Delinquent Duo": "Forbidden",
    "Dimension Fusion": "Forbidden",
    "Giant Trunade": "Forbidden",
    "Graceful Charity": "Forbidden",
    "Heavy Storm": "Forbidden",
    "Kaiser Colosseum": "Forbidden",
    "Last Will": "Forbidden",
    "Mass Driver": "Forbidden",
    "Mirage of Nightmare": "Forbidden",
    "Mystic Mine": "Forbidden",
    "Original Sinful Spoils - Snake-Eye": "Forbidden",
    "Painful Choice": "Forbidden",
    "Pot of Greed": "Forbidden",
    "Smoke Grenade of the Thief": "Forbidden",
    "Soul Charge": "Forbidden",
    "The Forceful Sentry": "Forbidden",
    "Appointer of the Red Lotus": "Forbidden",
    "Branded Expulsion": "Forbidden",
    "Dimensional Barrier": "Forbidden",
    "Harpie's Feather Storm": "Forbidden",
    "Imperial Order": "Forbidden",
    "Last Turn": "Forbidden",
    "Red Reboot": "Forbidden",
    "Return from the Different Dimension": "Forbidden",
    "Royal Oppression": "Forbidden",
    "Self-Destruct Button": "Forbidden",
    "Sixth Sense": "Forbidden",
    "Summon Limit": "Forbidden",
    "Trap Dustshoot": "Forbidden",
    "Ultimate Offering": "Forbidden",
    "Vanity's Emptiness": "Forbidden",
    // Limited
    "Ame no Habakiri no Mitsurugi": "Limited",
    "Astrograph Sorcerer": "Limited",
    "Bystial Druiswurm": "Limited",
    "Bystial Magnamhut": "Limited",
    "Cupsy★Yummy Way": "Limited",
    "Cupsy☆Yummy": "Limited",
    "Daigusto Emeral": "Limited",
    "Dimension Shifter": "Limited",
    "Dracotail Mululu": "Limited",
    "Exodia the Forbidden One": "Limited",
    "Ext Ryzeal": "Limited",
    "Fairy Tail - Snow": "Limited",
    "Fiendsmith's Lacrima": "Limited",
    "Gem-Knight Master Diamond": "Limited",
    "K9-66a Jokul": "Limited",
    "Keldo the Sacred Protector": "Limited",
    "Left Arm of the Forbidden One": "Limited",
    "Left Leg of the Forbidden One": "Limited",
    "Linkuriboh": "Limited",
    "Maliss <P> Dormouse": "Limited",
    "Maliss <P> White Rabbit": "Limited",
    "Mathmech Circular": "Limited",
    "Miscellaneousaurus": "Limited",
    "Mudora the Sword Oracle": "Limited",
    "Number 40: Gimmick Puppet of Strings": "Limited",
    "Number C40: Gimmick Puppet of Dark Strings": "Limited",
    "Phantom of Yubel": "Limited",
    "PSY-Framegear Gamma": "Limited",
    "PSY-Framelord Omega": "Limited",
    "Right Arm of the Forbidden One": "Limited",
    "Right Leg of the Forbidden One": "Limited",
    "Ryzeal Detonator": "Limited",
    "Striker Dragon": "Limited",
    "Substitoad": "Limited",
    "Summon Sorceress": "Limited",
    "Sunavalon Dryas": "Limited",
    "Sunvine Healer": "Limited",
    "T.G. Hyper Librarian": "Limited",
    "Tearlaments Havnis": "Limited",
    "Tearlaments Merrli": "Limited",
    "Tearlaments Scheiren": "Limited",
    "Tenpai Dragon Chundra": "Limited",
    "Vanquish Soul Hollie Sue": "Limited",
    "Yummy★Snatchy": "Limited",
    "Zoodiac Ratpier": "Limited",
    "\"A Case for K9\"": "Limited",
    "Bonfire": "Limited",
    "Branded Fusion": "Limited",
    "Brilliant Fusion": "Limited",
    "Called by the Grave": "Limited",
    "Card Destruction": "Limited",
    "Card of Demise": "Limited",
    "Chain Strike": "Limited",
    "Chaos Space": "Limited",
    "Chicken Game": "Limited",
    "Crossout Designator": "Limited",
    "Divine Wind of Mist Valley": "Limited",
    "Final Countdown": "Limited",
    "Foolish Burial": "Limited",
    "Gateway of the Six": "Limited",
    "Gold Sarcophagus": "Limited",
    "Harpie's Feather Duster": "Limited",
    "Infernity Launcher": "Limited",
    "Instant Fusion": "Limited",
    "Into the Void": "Limited",
    "Ketu Dracotail": "Limited",
    "Magical Mid-Breaker Field": "Limited",
    "Metamorphosis": "Limited",
    "Monster Gate": "Limited",
    "Monster Reborn": "Limited",
    "Obedience Schooled": "Limited",
    "One Day of Peace": "Limited",
    "One for One": "Limited",
    "Pot of Prosperity": "Limited",
    "Premature Burial": "Limited",
    "Radiant Typhoon Chant": "Limited",
    "Rahu Dracotail": "Limited",
    "Reasoning": "Limited",
    "Reinforcement of the Army": "Limited",
    "Sangen Kaimen": "Limited",
    "Sangen Summoning": "Limited",
    "Sekka's Light": "Limited",
    "Set Rotation": "Limited",
    "Sky Striker Mecha - Hornet Drones": "Limited",
    "Slash Draw": "Limited",
    "Synchro Overtake": "Limited",
    "Terraforming": "Limited",
    "That Grass Looks Greener": "Limited",
    "Triple Tactics Talent": "Limited",
    "Anti-Spell Fragrance": "Limited",
    "Gozen Match": "Limited",
    "Magical Explosion": "Limited",
    "Naturia Sacred Tree": "Limited",
    "Rivalry of Warlords": "Limited",
    "Skill Drain": "Limited",
    "Solemn Judgment": "Limited",
    "There Can Be Only One": "Limited",
    // Semi-Limited
    "Dracotail Arthalion": "Semi-Limited",
    "Dracotail Lukias": "Semi-Limited",
    "Droll & Lock Bird": "Semi-Limited",
    "Ice Ryzeal": "Semi-Limited",
    "Sword Ryzeal": "Semi-Limited",
    "Unchained Soul of Sharvara": "Semi-Limited",
    "Maliss in Underground": "Semi-Limited",
    "Purrely Delicious Memory": "Semi-Limited",
    "Purrely Sleepy Memory": "Semi-Limited",
    "Runick Fountain": "Semi-Limited",
  },
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FORMATS = [
  {
    key: "adv",
    label: "Advance Format",
    description: "Current OCG/TCG Advanced Format — Forbidden, Limited, and Semi-Limited.",
    placeholder: `{\n  "Pot of Greed": "Forbidden",\n  "Reinforcement of the Army": "Limited",\n  "Solemn Warning": "Semi-Limited"\n}`,
    bundled: BUNDLED_ADV,
  },
  {
    key: "edi",
    label: "Edison Format",
    description: "Edison Format (September 2010 list) — same three status tiers.",
    placeholder: `{\n  "Rescue Cat": "Forbidden",\n  "Cold Wave": "Forbidden",\n  "Charge of the Light Brigade": "Semi-Limited"\n}`,
    bundled: null,
  },
];

const STATUS_COLORS = {
  Forbidden: "text-danger",
  Limited: "text-amber-400",
  "Semi-Limited": "text-yellow-300",
};

// ---------------------------------------------------------------------------
// Per-format panel
// ---------------------------------------------------------------------------

const FormatPanel = ({ fmt }) => {
  const [loadState, setLoadState] = useState("idle"); // idle | loading | ready | error
  const [loadError, setLoadError] = useState("");

  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);

  // form state
  const [versionName, setVersionName] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [changeNotes, setChangeNotes] = useState("");
  const [cardsText, setCardsText] = useState("{}");

  // ui state
  const [formError, setFormError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoadState("loading");
    setLoadError("");
    try {
      const data = await apiGetBanlist(fmt.key);
      setActive(data?.active || null);
      setHistory(data?.history || []);
      if (data?.active) {
        setVersionName(data.active.version_name || "");
        setEffectiveDate(data.active.effective_date || "");
        setChangeNotes(data.active.change_notes || "");
        setCardsText(JSON.stringify(data.active.cards || {}, null, 2));
      }
      setLoadState("ready");
    } catch (e) {
      if (e?.status === 404) {
        // No banlist pushed yet — show empty push form
        setActive(null);
        setHistory([]);
        setLoadState("ready");
      } else {
        setLoadError(e?.message || "Failed to load banlist");
        setLoadState("error");
      }
    }
  }, [fmt.key]);

  useEffect(() => {
    load();
  }, [load]);

  const cardCount = useMemo(() => {
    try {
      const parsed = JSON.parse(cardsText || "{}");
      return typeof parsed === "object" && !Array.isArray(parsed)
        ? Object.keys(parsed).length
        : null;
    } catch {
      return null;
    }
  }, [cardsText]);

  const statusBreakdown = useMemo(() => {
    try {
      const parsed = JSON.parse(cardsText || "{}");
      if (typeof parsed !== "object" || Array.isArray(parsed)) return null;
      const counts = { Forbidden: 0, Limited: 0, "Semi-Limited": 0 };
      for (const v of Object.values(parsed)) {
        if (v in counts) counts[v]++;
      }
      return counts;
    } catch {
      return null;
    }
  }, [cardsText]);

  const loadBundled = () => {
    if (!fmt.bundled) return;
    setVersionName(fmt.bundled.version_name);
    setEffectiveDate(fmt.bundled.effective_date);
    setChangeNotes(fmt.bundled.change_notes || "");
    setCardsText(JSON.stringify(fmt.bundled.cards, null, 2));
    setSaved(false);
    setFormError("");
  };

  const onPush = async () => {
    setFormError("");
    setSaved(false);
    if (!versionName.trim()) {
      setFormError('Version name is required (e.g. "April 2026").');
      return;
    }
    let cards;
    try {
      cards = JSON.parse(cardsText || "{}");
      if (typeof cards !== "object" || Array.isArray(cards)) throw new Error();
    } catch {
      setFormError('Cards must be valid JSON — { "Card Name": "Forbidden" | "Limited" | "Semi-Limited" }');
      return;
    }
    setBusy(true);
    try {
      const data = await apiPutBanlist(fmt.key, {
        version_name: versionName.trim(),
        effective_date: effectiveDate.trim() || null,
        change_notes: changeNotes.trim() || null,
        cards,
      });
      setActive(data.active);
      setHistory(data.history || []);
      setSaved(true);
    } catch (e) {
      setFormError(e?.message || "Failed to push banlist");
    } finally {
      setBusy(false);
    }
  };

  const loadFromHistory = (entry) => {
    setVersionName(entry.version_name || "");
    setEffectiveDate(entry.effective_date || "");
    setChangeNotes(entry.change_notes || "");
    setCardsText(JSON.stringify(entry.cards || {}, null, 2));
    setSaved(false);
    setFormError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loadState === "loading") return <SkeletonTable rows={4} />;
  if (loadState === "error") {
    return (
      <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">
        {loadError}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* live status banner */}
      {loadState === "ready" && (
        <p className="text-xs text-celestial-muted/70">
          {active ? (
            <>
              Currently live:{" "}
              <span className="text-celestial-text font-medium">{active.version_name}</span>
              {active.effective_date && <> · effective {active.effective_date}</>}
              {active.updated_by && <> · pushed by {active.updated_by}</>}
              {active.published_at && (
                <> · {new Date(active.published_at).toLocaleDateString()}</>
              )}
            </>
          ) : (
            "No banlist pushed yet — mobile/web have no server-side list for this format."
          )}
        </p>
      )}

      {/* push form */}
      <div className="card-celestial rounded-2xl p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Version name"
            value={versionName}
            onChange={setVersionName}
            placeholder="April 2026"
          />
          <Field
            label="Effective date"
            value={effectiveDate}
            onChange={setEffectiveDate}
            placeholder="2026-04-01"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-celestial-muted">
            Change notes (optional)
          </label>
          <textarea
            value={changeNotes}
            onChange={(e) => setChangeNotes(e.target.value)}
            rows={2}
            placeholder="Notable changes in this list..."
            className="w-full px-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text placeholder:text-celestial-muted/40 text-sm focus:outline-none focus:border-glow-mid/50 resize-none"
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-celestial-muted">
              Cards (JSON — card name → status)
            </label>
            {fmt.bundled && (
              <button
                type="button"
                onClick={loadBundled}
                className="text-xs text-glow-light hover:text-glow-mid flex items-center gap-1"
              >
                <PackageOpen className="w-3 h-3" /> Load bundled list
              </button>
            )}
            <div className="flex items-center gap-3 text-xs text-celestial-muted/70">
              {statusBreakdown && (
                <>
                  {["Forbidden", "Limited", "Semi-Limited"].map((s) => (
                    statusBreakdown[s] > 0 && (
                      <span key={s} className={STATUS_COLORS[s]}>
                        {statusBreakdown[s]} {s}
                      </span>
                    )
                  ))}
                </>
              )}
              {cardCount !== null && (
                <span className="text-celestial-text">{cardCount} total</span>
              )}
            </div>
          </div>
          <textarea
            value={cardsText}
            onChange={(e) => setCardsText(e.target.value)}
            rows={18}
            spellCheck={false}
            placeholder={fmt.placeholder}
            className="w-full px-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text font-mono text-xs focus:outline-none focus:border-glow-mid/50"
          />
        </div>

        {formError && (
          <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">
            {formError}
          </div>
        )}
        {saved && !formError && (
          <div className="text-sm text-success bg-success/10 border border-success/30 rounded-xl px-3 py-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Live — apps will pick this up on next launch.
          </div>
        )}

        <Button
          onClick={onPush}
          disabled={busy}
          className="w-full btn-celestial rounded-xl py-2.5 flex items-center justify-center gap-2"
        >
          <Upload className="w-4 h-4" /> {busy ? "Pushing…" : "Push live"}
        </Button>
      </div>

      {/* history log */}
      {history.length > 0 && (
        <div className="card-celestial rounded-2xl overflow-hidden">
          <button
            onClick={() => setHistoryOpen((o) => !o)}
            className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium text-celestial-text hover:bg-surface/40 transition-colors"
          >
            <span className="flex items-center gap-2">
              <History className="w-4 h-4 text-celestial-muted" />
              Version history ({history.length})
            </span>
            {historyOpen ? (
              <ChevronUp className="w-4 h-4 text-celestial-muted" />
            ) : (
              <ChevronDown className="w-4 h-4 text-celestial-muted" />
            )}
          </button>

          {historyOpen && (
            <div className="divide-y divide-celestial-border/10 border-t border-celestial-border/10">
              {history.map((entry) => (
                <div
                  key={entry.id}
                  className="px-5 py-3.5 flex items-start justify-between gap-4"
                >
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-celestial-text">
                        {entry.version_name || "—"}
                      </span>
                      <StatusBadge status="archived">archived</StatusBadge>
                      {entry.effective_date && (
                        <span className="text-xs text-celestial-muted/70">
                          effective {entry.effective_date}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-celestial-muted/70">
                      {entry.card_count ?? 0} cards
                      {entry.updated_by && <> · pushed by {entry.updated_by}</>}
                      {entry.published_at && (
                        <> · {new Date(entry.published_at).toLocaleDateString(undefined, {
                          year: "numeric", month: "short", day: "numeric",
                        })}</>
                      )}
                    </div>
                    {entry.change_notes && (
                      <div className="text-xs text-celestial-muted/60 truncate max-w-md">
                        {entry.change_notes}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => loadFromHistory(entry)}
                    className="shrink-0 flex items-center gap-1 text-xs text-glow-light hover:text-glow-mid"
                    title="Load this version into the editor"
                  >
                    <RotateCcw className="w-3 h-3" /> Load
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const BanlistManager = () => {
  const [activeTab, setActiveTab] = useState("adv");
  const fmt = FORMATS.find((f) => f.key === activeTab);

  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader
        title="Banlist Manager"
        subtitle="Push per-format banlists live to mobile + web — no release needed. Previous versions are kept as history."
      />

      {/* format tabs */}
      <div className="flex gap-1 p-1 bg-surface/40 rounded-xl border border-celestial-border/15 w-fit">
        {FORMATS.map((f) => (
          <button
            key={f.key}
            onClick={() => setActiveTab(f.key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === f.key
                ? "bg-glow-mid/20 text-celestial-text"
                : "text-celestial-muted hover:text-celestial-text"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <p className="text-xs text-celestial-muted/70 -mt-2">{fmt.description}</p>

      {/* mount a fresh panel per tab key so state resets on switch */}
      <FormatPanel key={activeTab} fmt={fmt} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared field component
// ---------------------------------------------------------------------------

const Field = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-celestial-muted">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text placeholder:text-celestial-muted/40 focus:outline-none focus:border-glow-mid/50"
    />
  </div>
);

export default BanlistManager;
